import { createFileRoute, Link } from "@tanstack/react-router";
import { lastmodFor } from "@/lib/route-lastmod";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ORG_REF, PHYSICIAN_REF } from "@/lib/medical-schema";


const URL = `${SITE}/es/requisitos-para-cirugia`;

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Califico para la cirugía para bajar de peso?",
    a: "Lo más probable es que califique si su IMC es de 40 o más, o de 35 o más con una condición relacionada con la obesidad como diabetes tipo 2, presión alta o apnea del sueño.\n\nLos pacientes de pago propio califican con un IMC de 35 o más solo por el peso, o de 30 a 34.9 con una condición médica que califique.\n\nLa forma más rápida de confirmarlo es una consulta donde revisamos su IMC, su historial médico y su plan de seguro.",
  },
  {
    q: "¿Qué condiciones médicas cuentan para calificar?",
    a: "Con un IMC de 35 a 39.9 usando seguro, se necesita al menos una condición relacionada con la obesidad que califique.\n\nLas condiciones que la mayoría de los planes aceptan incluyen diabetes tipo 2, presión arterial alta, apnea obstructiva del sueño, enfermedad de las arterias coronarias u otra enfermedad del corazón, colesterol alto u otro trastorno de los lípidos, y osteoartritis severa o enfermedad de las articulaciones que soportan peso.\n\nTambién pueden calificar la enfermedad del hígado graso, el reflujo gastroesofágico severo, y la hipertensión pulmonar o síndrome de hipoventilación por obesidad.\n\nSi el seguro está pagando, la aseguradora decide qué problemas médicos pueden calificarlo, y cada aseguradora tiene reglas diferentes. La diabetes tipo 2, la hipertensión y la apnea del sueño cuentan como condiciones calificantes ante todas las aseguradoras de salud. Las demás de la lista pueden bastar, pero también puede que no.\n\nEs importante comunicarse con su aseguradora y asegurarse.\n\nLa mayoría de las aseguradoras publican una política médica con sus reglas. Por lo general puede encontrarla en línea buscando en Google: \"Medical policy for bariatric surgery for [my insurance company].\"\n\nPuede que califique sin saberlo: estudios han demostrado que más del 70% de las personas con un IMC entre 35 y 39.9 tienen apnea obstructiva del sueño, y muchas no han sido evaluadas.",
  },
  {
    q: "¿Por qué los umbrales de IMC son diferentes si tengo seguro?",
    a: "La mayoría de los criterios de las aseguradoras se basan en una declaración de consenso de los Institutos Nacionales de Salud de 1991: IMC de 40 o más, o de 35 o más con una comorbilidad.\n\nEn 2022, ASMBS e IFSO publicaron indicaciones actualizadas.\n\nRecomiendan la cirugía para cualquier persona con un IMC de 35 o más sin importar las condiciones relacionadas, y para personas con enfermedad metabólica con un IMC de 30 a 34.9.\n\nCuando usted paga de su bolsillo, rigen las guías clínicas actuales; cuando paga el seguro, rige la política del plan.",
  },
  {
    q: "¿Los umbrales de IMC son diferentes para pacientes de ascendencia del este de Asia?",
    a: "Sí.\n\nA cualquier IMC dado, las personas de ascendencia del este de Asia tienden a tener más grasa visceral y mayor riesgo de diabetes tipo 2 y enfermedad cardiovascular.\n\nLas guías de 2022 de ASMBS e IFSO recomiendan ajustar las zonas de riesgo del IMC para definir la obesidad a partir de 25 a 27.5 en esta población.\n\nEn la práctica, un paciente de ascendencia del este de Asia con un IMC de 32 puede ser un candidato quirúrgico completamente apropiado. Las pólizas de seguro rara vez reconocen este ajuste, así que suele importar más en la conversación de pago propio.",
  },
  {
    q: "¿Califico para la manga gástrica endoscópica (ESG)?",
    a: "La ESG está autorizada por la FDA para adultos con un IMC de 30 a 50 que no han podido bajar de peso o mantener la pérdida de peso con medidas más conservadoras.\n\nCasi siempre es de pago propio.\n\nA diferencia de la manga gástrica y el bypass gástrico, que tienen un piso pero no un límite superior de IMC, la ESG tiene un límite inferior y uno superior.",
  },
  {
    q: "¿Califico para el balón intragástrico?",
    a: "El balón gástrico (Orbera) está aprobado por la FDA para un IMC de 30 a 40, con intentos previos de pérdida de peso conservadores y participación en un programa supervisado de dieta y conducta.\n\nEl balón permanece colocado seis meses.\n\nPor lo general el seguro no lo cubre y casi siempre es de pago propio.",
  },
];

export const Route = createFileRoute("/es/requisitos-para-cirugia")({
  head: () => {
    const base = esHead("qualifications");
    return {
      ...base,
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    ...esWebPageSchema("qualifications"),
                    "@type": "MedicalWebPage",
                    about: { "@id": "https://virginiabariatric.com/#condition-obesity" },
                    mainEntity: { "@id": `${URL}#faq` },
                    significantLink: [`${URL}#faq`, `${URL}#imc-y-pago`],
                    audience: {
                      "@type": "MedicalAudience",
                      audienceType: "Patient",
                      geographicArea: {
                        "@type": "AdministrativeArea",
                        name: "Northern Virginia",
                      },
                    },
                    specialty: "https://schema.org/Surgical",
                    lastReviewed: "2026-08-18",
                    dateModified: lastmodFor("/es/requisitos-para-cirugia") ?? "2026-09-03",
                    reviewedBy: PHYSICIAN_REF,
                    author: PHYSICIAN_REF,
                    publisher: ORG_REF,
                    speakable: {
                      "@type": "SpeakableSpecification",
                      cssSelector: ["h1", "h2"],
                    },
                  },
                  {
                    "@type": "FAQPage",
                    "@id": `${URL}#faq`,
                    url: URL,
                    inLanguage: "es-US",
                    isPartOf: { "@id": `${URL}#webpage` },
                    publisher: ORG_REF,
                    mainEntity: FAQS.map((f, i) => ({
                      "@type": "Question",
                      "@id": `${URL}#faq-${i + 1}`,
                      url: `${URL}#faq-${i + 1}`,
                      name: f.q,
                      inLanguage: "es-US",
                      acceptedAnswer: {
                        "@type": "Answer",
                        text: f.a,
                        url: `${URL}#faq-${i + 1}`,
                      },
                    })),
                  },
                  {
                    "@type": "HowTo",
                    name: "Cómo saber si califica para la cirugía bariátrica",
                    description:
                      "Los cuatro pasos para confirmar su candidatura a la cirugía bariátrica en Virginia Bariatric Surgery, en el norte de Virginia.",
                    url: URL,
                    inLanguage: "es-US",
                    step: [
                      {
                        "@type": "HowToStep",
                        name: "Calcule su IMC",
                        url: `${URL}#que-hacer`,
                        text: "Calcule su índice de masa corporal. Toma diez segundos y le indica en qué fila de elegibilidad se encuentra.",
                      },
                      {
                        "@type": "HowToStep",
                        name: "Averigüe si tiene una condición que califique",
                        url: `${URL}#que-hacer`,
                        text: "Si no le han evaluado para apnea del sueño, diabetes o hígado graso, puede tener una condición que califique y no saberlo.",
                      },
                      {
                        "@type": "HowToStep",
                        name: "Llame a su aseguradora",
                        url: `${URL}#que-hacer`,
                        text: "Haga dos preguntas: ¿mi plan incluye beneficio de cirugía bariátrica?, y ¿qué IMC y documentación exige?",
                      },
                      {
                        "@type": "HowToStep",
                        name: "Venga a una consulta",
                        url: `${URL}#que-hacer`,
                        text: "Confirmaremos en qué punto está, pediremos lo que falte y le daremos una respuesta directa sobre qué opciones están abiertas para usted.",
                      },
                    ],
                  },
                ],

              }),
            },
          ]
        : [],
    };
  },
  component: EsQualificationsPage,
});

const thClass = "border border-border bg-muted/40 px-4 py-2 font-semibold text-left";
const tdClass = "border border-border px-4 py-2";
const tdHeadClass = "border border-border px-4 py-2 font-semibold";

function EsQualificationsPage() {
  return (
    <EsPageShell
      page="qualifications"
      title="¿Califica para la cirugía bariátrica?"
      intro="Depende de su estado médico, su IMC y de si planea usar seguro. Aquí se lo explicamos en español, en lenguaje sencillo."
    >
      <EsSection heading="En qué nos fijamos">
        <p>
          Si quiere saber si califica para la cirugía para bajar de peso, la respuesta
          se reduce a unas pocas preguntas sencillas. Esto es lo que evaluamos:
        </p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>
            <strong>Su edad</strong> — ofrecemos cirugía para bajar de peso a
            personas de 18 años en adelante.
          </li>
          <li>
            <strong>Su aptitud médica y emocional</strong> – ¿es un buen momento para
            una cirugía que cambia vidas?
          </li>
          <li>
            <strong>Su peso y su estatura</strong> — estas cifras nos dan su índice
            de masa corporal (IMC).
          </li>
          <li>
            <strong>Enfermedades médicas relacionadas con la obesidad</strong> — ¿tiene
            alguna?
          </li>
          <li>
            <strong>Quién paga</strong> — ¿usa seguro o paga de su bolsillo?
          </li>
        </ol>
      </EsSection>

      <EsSection id="edad-y-aptitud" heading="Edad y aptitud médica">
        <p>
          Estas pautas aplican a todos, sin importar el IMC o quién pague. Son
          requisitos de entrada. Cumplirlos significa que se le puede considerar
          para cirugía. Si no los cumple, no se le puede ofrecer la cirugía, al
          menos hasta que algo cambie.
          Incluyen:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Tener 18 años o más.</li>
          <li>
            Gozar de buena salud para someterse a la cirugía y a la anestesia
            general. Esto se evalúa en la evaluación preoperatoria. Podríamos
            necesitar que se realice pruebas cardíacas o pulmonares según su
            historial.
          </li>
          <li>
            Estar psicológicamente preparado. Se confirma con una evaluación de un
            profesional calificado en salud conductual.
          </li>
          <li>No tener abuso activo de sustancias.</li>
          <li>
            No estar embarazada y estar dispuesta a posponer el embarazo durante
            aproximadamente 12 a 18 meses después de la cirugía.
          </li>
          <li>Entender los riesgos.</li>
          <li>
            Estar dispuesto a comprometerse con cambios alimenticios de por vida,
            visitas de seguimiento y suplementos vitamínicos diarios.
          </li>
        </ul>
        <p>
          La mayoría de las personas que quieren la cirugía pueden cumplir estos
          requisitos, aunque a veces hay trabajo que hacer. Un problema de salud
          conductual o un problema médico sin tratar suele significar un retraso,
          no un no permanente.
        </p>
      </EsSection>

      <EsSection id="imc-y-pago" heading="IMC, enfermedad médica y quién paga" maxWidth="7xl">
        <p>
          Los requisitos de IMC y condiciones médicas dependen de quién paga. Si
          paga el seguro, estos son los requisitos:
        </p>

        <h3 className="text-center font-serif text-xl text-brand-deep pt-1">
          Si paga el seguro
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-base">
            <thead>
              <tr>
                <th className={thClass}>Su IMC</th>
                <th className={thClass}>¿Se necesitan condiciones médicas?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdHeadClass}>40 o más</td>
                <td className={tdClass}>No.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>35 – 39.9</td>
                <td className={tdClass}>Sí. Sin un diagnóstico que califique, la mayoría de los planes rechazará la solicitud.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>30 – 34.9</td>
                <td className={tdClass}>Sí, pero la mayoría de los planes no cubre la cirugía con este IMC. Unos pocos, muy raros, la cubren únicamente si hay diabetes tipo 2.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>Menos de 30</td>
                <td className={tdClass}>No es candidato para cirugía bariátrica.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-center font-serif text-xl text-brand-deep pt-1">
          Si paga de su bolsillo
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-base">
            <thead>
              <tr>
                <th className={thClass}>Su IMC</th>
                <th className={thClass}>¿Se necesitan condiciones médicas?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdHeadClass}>40 o más</td>
                <td className={tdClass}>No.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>35 – 39.9</td>
                <td className={tdClass}>No.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>30 – 34.9</td>
                <td className={tdClass}>Sí. Usted es candidato si tiene enfermedad médica.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>Menos de 30</td>
                <td className={tdClass}>No es candidato para cirugía bariátrica.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Si las dos tablas le parecen diferentes para el mismo IMC, las está
          leyendo bien. La razón se explica más abajo.
        </p>
      </EsSection>

      <EsSection id="condiciones" heading="Qué condiciones cuentan">
        <p>
          Esto solo importa en la tabla del seguro. Con un IMC de 35 a 39.9 se
          necesita al menos una condición relacionada con la obesidad que
          califique. Las condiciones que la mayoría de los planes acepta:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Diabetes tipo 2</li>
          <li>Presión arterial alta</li>
          <li>Apnea obstructiva del sueño</li>
          <li>Enfermedad de las arterias coronarias u otra enfermedad del corazón</li>
          <li>Colesterol alto u otro trastorno de los lípidos</li>
          <li>Osteoartritis severa o enfermedad de las articulaciones que soportan peso</li>
          <li>Enfermedad del hígado graso</li>
          <li>Reflujo gastroesofágico severo</li>
          <li>Hipertensión pulmonar o síndrome de hipoventilación por obesidad</li>
        </ul>
        <p>
          Si el seguro está pagando, la aseguradora decide qué problemas médicos
          pueden calificarlo, y cada aseguradora tiene reglas diferentes. La
          diabetes tipo 2, la hipertensión y la apnea del sueño cuentan como
          condiciones calificantes ante todas las aseguradoras de salud. Las demás
          de la lista <em>pueden</em> bastar, pero también puede que no.
        </p>
        <p>
          Es importante comunicarse con su aseguradora y asegurarse.
        </p>
        <p>
          La mayoría de las aseguradoras publican una política médica con sus
          reglas. Por lo general puede encontrarla en línea buscando en Google:{" "}
          "Medical policy for bariatric surgery for [my insurance company]."
        </p>
        <aside
          aria-labelledby="califique-sin-saberlo"
          className="mt-6 rounded-xl border border-brand bg-brand-soft p-5 md:p-6"
        >
          <h3 id="califique-sin-saberlo" className="text-center font-serif text-xl text-brand-deep mb-2">
            Puede que califique sin saberlo.
          </h3>
          <p className="text-brand-deep">
            No asuma que no tiene una condición que califique. Un diagnóstico que
            califica a menudo está ahí esperando a que alguien lo busque.
          </p>
          <p className="mt-3 text-brand-deep">
            Por ejemplo, estudios han demostrado que más del 70% de las personas con
            un IMC entre 35 y 39.9 tienen apnea obstructiva del sueño.<sup>1</sup> Si
            nunca le han evaluado para eso, es prudente revisar. Puede que ya califique
            médicamente sin saberlo.
          </p>
        </aside>
      </EsSection>

      <EsSection id="por-que-difieren" heading="Por qué los umbrales de IMC son diferentes si tiene seguro">
        <p>
          Esta es la parte que más confunde a la gente, y la explicación es
          sencilla.
        </p>
        <p>
          La mayoría de los criterios de las aseguradoras se basa en una
          declaración de consenso de los Institutos Nacionales de Salud publicada
          en 1991.
        </p>
        <p>
          Esa declaración fijó un IMC de 40 o más, o de 35 o más con una
          comorbilidad. Esos umbrales han estado incorporados en las políticas de
          las aseguradoras por más de treinta años.
        </p>
        <p>
          La ciencia médica ha avanzado. En 2022, la Sociedad Americana de Cirugía
          Metabólica y Bariátrica y la Federación Internacional para la Cirugía de
          la Obesidad publicaron indicaciones actualizadas.
        </p>
        <p>
          Esas guías recomiendan la cirugía para cualquier persona con un IMC de
          35 o más, tenga o no una condición relacionada. También señalan que la
          cirugía debe considerarse en personas con enfermedad metabólica y un IMC
          de 30 a 34.9.
        </p>
        <p>
          Cuando usted paga de su bolsillo, rigen las guías clínicas actuales.
          Cuando paga el seguro, rige la política del plan. Lamentablemente, la
          mayoría de los planes todavía usa los umbrales antiguos.
        </p>
      </EsSection>

      <EsSection heading="Dos salvedades que conviene conocer" maxWidth="7xl">
        <h3 id="este-de-asia" className="text-center font-serif text-xl text-brand-deep pt-1">
          1. Si usted es de ascendencia del este de Asia, los umbrales son más bajos
        </h3>
        <p>
          El índice de masa corporal no conlleva el mismo riesgo metabólico en
          todas las poblaciones.
        </p>
        <p>
          A cualquier IMC dado, las personas de ascendencia del este de Asia
          tienden a tener más grasa visceral y mayor riesgo de diabetes tipo 2 y
          enfermedad cardiovascular que las personas de ascendencia europea con el
          mismo número.
        </p>
        <p>
          Las guías de 2022 de ASMBS e IFSO toman esto en cuenta directamente.
          Recomiendan ajustar las zonas de riesgo del IMC para definir la obesidad
          a partir de un umbral de 25 a 27.5 en esta población.
        </p>
        <p>
          También señalan que no se debe negar el acceso a la cirugía únicamente
          por los umbrales tradicionales de IMC.
        </p>
        <p>
          En la práctica, esto significa que un paciente de ascendencia del este de
          Asia con un IMC de 32 puede ser un candidato quirúrgico completamente
          apropiado, donde el mismo número en otro paciente no lo sería.
        </p>
        <p>
          Las pólizas de seguro rara vez reconocen este ajuste, así que suele
          importar más en la conversación de pago propio. Vale la pena mencionarlo
          en su consulta de cualquier manera.
        </p>

        <h3 id="endoscopicos" className="text-center font-serif text-xl text-brand-deep pt-1">
          2. Los procedimientos transorales tienen sus propias ventanas de IMC — con techo incluido
        </h3>
        <p>
          La manga gástrica y el bypass gástrico tienen un piso pero no un límite
          superior.
        </p>
        <p>
          Los procedimientos endoscópicos, realizados por la boca sin incisiones,
          funcionan de otra manera: están aprobados para rangos de IMC definidos,
          con límite inferior y superior. Por encima del techo, no son la herramienta
          adecuada.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-base">
            <thead>
              <tr>
                <th className={thClass}>Procedimiento</th>
                <th className={thClass}>Ventana de IMC</th>
                <th className={thClass}>Notas</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdHeadClass}>Manga gástrica endoscópica (ESG)</td>
                <td className={tdClass}>30 – 50</td>
                <td className={tdClass}>
                  La autorización de la FDA especifica un IMC de 30 a 50 en adultos
                  que no han podido bajar de peso o mantener la pérdida de peso con
                  medidas más conservadoras. Casi siempre de pago propio.
                </td>
              </tr>
              <tr>
                <td className={tdHeadClass}>
                  <Link to="/es/balon-gastrico" className="text-brand-mid underline">
                    Balón gástrico (Orbera)
                  </Link>
                </td>

                <td className={tdClass}>30 – 40</td>
                <td className={tdClass}>
                  La aprobación de la FDA especifica un IMC de 30 a 40, intentos
                  previos de pérdida de peso conservadores y participación en un
                  programa supervisado de dieta y conducta. El balón permanece
                  colocado seis meses. Casi siempre de pago propio.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          En general, el seguro no cubre ninguna de las dos opciones endoscópicas,
          así que para ellas la tabla relevante es la de pago propio.
        </p>
        <p>
          Vale la pena considerarlas si su IMC está entre 30 y 40, un rango donde
          la cobertura puede limitar las opciones quirúrgicas y las opciones
          endoscópicas están claramente en etiqueta.
        </p>
      </EsSection>

      <EsSection id="que-hacer" heading="Qué hacer a continuación">
        <ol className="list-decimal pl-6 space-y-1">
          <li>
            <strong>Calcule su IMC.</strong> Toma diez segundos y le dice en qué
            fila está.
          </li>
          <li>
            <strong>Averigüe si tiene una condición que califique.</strong> Si no le
            han evaluado para apnea del sueño, diabetes o hígado graso, puede
            tener una y no saberlo.
          </li>
          <li>
            <strong>Llame a su aseguradora</strong> y haga dos preguntas: ¿mi plan
            incluye beneficio de cirugía bariátrica?, y ¿qué IMC y documentación
            exige? Si el beneficio no existe, eso cambia toda la conversación
            temprano y no tarde. Nuestra{" "}
            <Link to="/es/seguro-para-cirugia-bariatrica" className="text-brand-mid underline">
              guía de seguro para cirugía bariátrica
            </Link>{" "}
            tiene los detalles plan por plan para{" "}
            <Link to="/es/cobertura-de-seguro" className="text-brand-mid underline">
              Virginia
            </Link>{" "}
            y{" "}
            <Link to="/es/seguro/carolina-del-norte" className="text-brand-mid underline">
              Carolina del Norte
            </Link>
            , pero otros estados llegarán pronto. Más de 1,000 pacientes han
            viajado a nuestra consulta desde Maryland, Delaware y Virginia
            Occidental, y algunos vienen de aún más lejos. (Pacientes han viajado
            desde Canadá, Europa y Medio Oriente para operarse con el Dr. Fitzer.)
          </li>
          <li>
            <strong>Venga a una consulta.</strong> Confirmaremos en qué punto está,
            pediremos lo que falte y le daremos una respuesta directa sobre qué
            opciones realmente están abiertas para usted.
          </li>
        </ol>

        <section
          aria-labelledby="donde-esta-parado"
          className="rounded-xl border border-border bg-muted/40 p-5 text-center md:p-6"
        >
          <h3 id="donde-esta-parado" className="font-serif text-xl text-brand-deep">
            Averigüe en qué punto está
          </h3>
          <p className="mt-2">
            Traiga su tarjeta del seguro y los registros médicos que tenga. Si la cirugía no es la
            respuesta correcta para usted, se lo diremos, y hablaremos de lo que sí lo es.
          </p>
          <p className="mt-4 flex flex-wrap justify-center gap-3">
            <Link
              to="/es/contacto"
              className="inline-flex items-center rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Pedir una consulta
            </Link>
            <Link
              to="/es/seguro-para-cirugia-bariatrica"
              className="inline-flex items-center rounded-full border border-brand-deep px-7 py-3 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
            >
              Guía de cobertura de seguro
            </Link>
          </p>
        </section>

        <p className="text-sm text-muted-foreground">
          Esta página es información general, no consejo médico ni garantía de
          beneficios del seguro. La candidatura individual se determina mediante
          evaluación clínica, y los criterios de cobertura varían según el plan.
          Revisada el 18 de agosto de 2026.
        </p>

      </EsSection>

      <EsSection id="fuentes" heading="Fuentes">
        <ol className="list-decimal pl-6 space-y-1.5">
          <li>
            Lopez PP, Stefan B, Schulman CI, Byers PM. Prevalencia de apnea del sueño en pacientes con obesidad mórbida que se presentaron a evaluación de cirugía para bajar de peso: más evidencia para el tamizaje rutinario de apnea obstructiva del sueño antes de la cirugía para bajar de peso. <em>Am Surg.</em> 2008 Sep;74(9):834-8. PMID: 18807673.
          </li>
          <li>
            Eisenberg D, Shikora SA, Aarts E, et al. Indicaciones de 2022 de la
            Sociedad Americana de Cirugía Metabólica y Bariátrica (ASMBS) y la
            Federación Internacional para la Cirugía de la Obesidad y Trastornos
            Metabólicos (IFSO) para la cirugía metabólica y bariátrica.{" "}
            <em>Surg Obes Relat Dis.</em> 2022.
          </li>
          <li>
            Conferencia de Consenso de los NIH, Cirugía Gastrointestinal para la
            Obesidad Severa, 1991 — el origen de los umbrales IMC 40 / IMC 35 con
            comorbilidad que la mayoría de las aseguradoras todavía usa.
          </li>
          <li>
            FDA, Sistema de Balón Intragástrico ORBERA, Resumen de Datos de
            Seguridad y Efectividad, P140008 (indicaciones de uso, IMC 30–40).
          </li>
          <li>
            FDA, clasificación De Novo DEN210045, sistemas Apollo ESG y Apollo ESG
            Sx (indicaciones de uso, IMC 30–50).
          </li>
          <li>ASMBS, Procedimientos Avalados y Dispositivos Aprobados por la FDA.</li>
        </ol>
      </EsSection>

      <EsSection id="faq" heading="Preguntas frecuentes">
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <h3 className="font-semibold text-foreground">{f.q}</h3>
              <div className="mt-1 space-y-2">
                {f.a.split("\n\n").map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </EsSection>
    </EsPageShell>
  );
}
